import { useState } from "react";
import { Copy, Check, Mail, MessageSquare } from "lucide-react";
import { toast } from "sonner";

// Highlights [PLACEHOLDER] tokens in orange so organizers remember to fill them.
function renderWithPlaceholders(text) {
  const parts = text.split(/(\[[^\]]+\])/g);
  return parts.map((part, i) =>
    /^\[[^\]]+\]$/.test(part) ? (
      <span key={i} className="placeholder-token">{part}</span>
    ) : (
      <span key={i}>{part}</span>
    )
  );
}

function TemplateCard({ template }) {
  const [copied, setCopied] = useState(false);
  const fullText = template.subject ? `Subject: ${template.subject}\n\n${template.body}` : template.body;

  async function copy() {
    try {
      await navigator.clipboard.writeText(fullText);
    } catch (e) {
      // Fallback for browsers/contexts without clipboard API
      const ta = document.createElement("textarea");
      ta.value = fullText;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    toast.success("Copied to clipboard", { description: `${template.title} is ready to paste.` });
    setTimeout(() => setCopied(false), 2000);
  }

  const Icon = template.channel === "Email" ? Mail : MessageSquare;

  return (
    <div className="rounded-2xl border border-brand-navy/10 bg-white pib-shadow-sm overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-4 py-3 bg-brand-cream border-b border-brand-cream-dark">
        <div className="flex items-center gap-2 min-w-0">
          <Icon className="w-4 h-4 text-brand-orange flex-shrink-0" />
          <span className="font-semibold text-brand-navy truncate">{template.title}</span>
        </div>
        <button
          type="button"
          data-testid={`copy-${template.id}`}
          onClick={copy}
          className={`flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold min-h-[40px] transition-colors ${
            copied ? "bg-brand-navy text-white" : "bg-brand-orange text-white hover:bg-brand-orange-dark"
          }`}
        >
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <div className="p-4">
        {template.subject && (
          <p className="text-sm mb-2">
            <span className="font-semibold text-brand-navy/60">Subject: </span>
            <span className="text-brand-navy">{renderWithPlaceholders(template.subject)}</span>
          </p>
        )}
        <p className="text-[15px] leading-relaxed text-brand-navy whitespace-pre-wrap">
          {renderWithPlaceholders(template.body)}
        </p>
      </div>
    </div>
  );
}

export default function PromotionTemplates({ templates }) {
  return (
    <div className="space-y-4" data-testid="promotion-templates">
      {templates.map((t) => (
        <TemplateCard key={t.id} template={t} />
      ))}
    </div>
  );
}
